import { useQuery } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { StateView } from '@/card/record/StateView'
import { Icon } from '@/components/Icon'
import { formatDate, formatNumber } from '@/lib/format'
import { fetchPage } from '@/lists/api'
import { Pager, useClientPages } from '@/lists/Pager'
import { fetchAbcXyz, fetchDeadStock, fetchPickerPerformance, fetchSupplierQuality } from './api'
import {
  initials,
  Money,
  num,
  NumberValue,
  percentText,
  PeriodBar,
  periodLabel,
  periodOf,
  QueryState,
  ShortId,
  shiftPeriod,
  SkeletonLines,
  type StepPeriod,
  useNames,
} from './reportKit'
import { iso } from './types'
import type { AbcClass, DeadStockRow, InventoryClassification, PickerPerformance, SupplierQuality, XyzClass } from './types'

/**
 * Складские отчёты (StockReports.dc.html; ТЗ §4.6, тикет #76).
 *
 * ## ABC/XYZ — матрицей, неликвид — отдельно
 *
 * ABC — по накопленной доле в обороте в деньгах, XYZ — по коэффициенту вариации. Товар с
 * одним месяцем истории — `UNKNOWN`, а не X: он в матрицу не попадает и назван отдельно.
 * Неликвид — не «класс C»: не двигавшийся полгода товар — это замороженные деньги, и одна
 * таблица позволила бы первому спрятаться во втором.
 *
 * ## NULL — прочерком
 *
 * Оборачиваемость при нулевом остатке и производительность при нулевом времени работы —
 * `null`, а не бесконечность и не ноль: закончившийся товар не самый оборачиваемый, а
 * сборщик без отметок времени не самый быстрый.
 *
 * ## Чего нет из макета
 *
 * Выбора склада (списка складов в API нет), оборачиваемости по категориям (сервер считает её
 * по позициям), стоимости неликвида и предложений «уценить / переместить / вернуть»,
 * простоев сборщиков, опозданий и рейтинга поставщиков, экспорта.
 */

const DEAD_DAYS = 180

export function WarehouseReportsPage() {
  const { t, locale, user } = useApp()
  const [period, setPeriod] = useState<StepPeriod>(() => periodOf('quarter', new Date()))
  const range = { from: period.from, to: period.to }

  const mayStock = can(user, 'warehouse.stock', 'read')
  const mayPick = can(user, 'warehouse.pick', 'read')
  const mayReceipts = can(user, 'warehouse.receipt', 'read')

  const abc = useQuery({
    queryKey: ['abc-xyz', range],
    enabled: mayStock,
    queryFn: ({ signal }) => fetchAbcXyz(range, null, signal),
    retry: false,
  })
  const deadSince = (() => {
    const date = new Date()
    date.setDate(date.getDate() - DEAD_DAYS)
    return iso(date)
  })()
  const dead = useQuery({
    queryKey: ['dead-stock', deadSince],
    enabled: mayStock,
    queryFn: ({ signal }) => fetchDeadStock(deadSince, 100, signal),
    retry: false,
  })
  const pickers = useQuery({
    queryKey: ['picker-performance', range],
    enabled: mayPick,
    queryFn: ({ signal }) => fetchPickerPerformance(range, signal),
    retry: false,
  })
  const suppliers = useQuery({
    queryKey: ['supplier-quality', range],
    enabled: mayReceipts,
    queryFn: ({ signal }) => fetchSupplierQuality(range, signal),
    retry: false,
  })

  const label = periodLabel(period, t, locale)
  if (!mayStock && !mayPick && !mayReceipts) {
    return (
      <div className="stack" style={{ gap: 14 }}>
        <Head />
        <StateView kind="forbidden" boxed title={t('wr.forbidden.title')} description={t('wr.forbidden.text')} />
      </div>
    )
  }

  const periodEmpty =
    (!mayStock || (abc.isSuccess && abc.data.length === 0)) &&
    (!mayPick || (pickers.isSuccess && pickers.data.length === 0)) &&
    (!mayReceipts || (suppliers.isSuccess && suppliers.data.length === 0))

  return (
    <div className="stack" style={{ gap: 14 }}>
      <Head>
        <PeriodBar value={period} onChange={setPeriod} />
      </Head>
      <p className="page-lead">{t('wr.lead')}</p>

      {periodEmpty ? (
        <StateView
          kind="empty"
          boxed
          title={t('wr.empty.title', { period: label })}
          description={t('wr.empty.text')}
          actions={
            <button className="btn btn-secondary btn-sm" type="button" onClick={() => setPeriod(shiftPeriod(period, -1))}>
              <Icon name="arrow-left" size="sm" />
              {t('sr.empty.prev', { period: periodLabel(shiftPeriod(period, -1), t, locale) })}
            </button>
          }
        />
      ) : (
        <div className="wa-split">
          <section className="panel" aria-labelledby="wr-turn" style={{ overflow: 'hidden' }}>
            <div className="panel-h">
              <span className="h2" id="wr-turn">{t('wr.turn.title')}</span>
              <span className="count">{label}</span>
            </div>
            {!mayStock ? (
              <StateView kind="forbidden" compact title={t('state.forbidden.title')} />
            ) : abc.isLoading ? (
              <SkeletonLines rows={6} />
            ) : abc.error ? (
              <QueryState compact error={abc.error} errorTitle={t('wr.abc.error')} onRetry={() => void abc.refetch()} />
            ) : (
              <TurnoverTable rows={abc.data ?? []} />
            )}
          </section>

          <section className="panel" aria-labelledby="wr-abc">
            <div className="panel-h">
              <span className="h2" id="wr-abc">{t('wr.abc.title')}</span>
              {abc.isSuccess && <span className="count">{t('wr.abc.count', { count: abc.data.length })}</span>}
            </div>
            {!mayStock ? (
              <StateView kind="forbidden" compact title={t('state.forbidden.title')} />
            ) : abc.isLoading ? (
              <SkeletonLines rows={6} />
            ) : abc.error ? (
              <QueryState compact error={abc.error} errorTitle={t('wr.abc.error')} onRetry={() => void abc.refetch()} />
            ) : (
              <Matrix rows={abc.data ?? []} />
            )}
          </section>
        </div>
      )}

      <DeadStockPanel allowed={mayStock} query={dead} since={deadSince} />
      <PickersPanel allowed={mayPick} query={pickers} periodText={label} />
      <SuppliersPanel allowed={mayReceipts} query={suppliers} periodText={label} />
    </div>
  )
}

function Head({ children }: { children?: ReactNode }) {
  const { t } = useApp()
  return (
    <>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t('nav.section.analytics')}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{t('nav.warehouseReports')}</span>
      </nav>
      <div className="page-head">
        <h1 className="h1">{t('nav.warehouseReports')}</h1>
        <span className="spacer" />
        {children}
      </div>
    </>
  )
}

interface QueryLike<T> {
  isLoading: boolean
  error: unknown
  data?: T
  refetch: () => unknown
}

// ------------------------------------------------------------------ оборачиваемость и ABC/XYZ

function TurnoverTable({ rows }: { rows: InventoryClassification[] }) {
  const { t } = useApp()
  // Медленные сверху: отчёт ради них и открывают. null (нулевой остаток) — в конце.
  const sorted = rows.slice().sort((a, b) => {
    const left = num(a.turnoverRatio)
    const right = num(b.turnoverRatio)
    if (left == null) return right == null ? 0 : 1
    if (right == null) return -1
    return left - right
  })
  const { visible, pager } = useClientPages(sorted, 25)
  if (rows.length === 0) return <StateView kind="empty" compact title={t('wr.abc.empty')} />
  return (
    <>
      <div className="wa-scroll">
        <table className="tbl" aria-label={t('wr.turn.title')}>
          <thead>
            <tr>
              <th>{t('report.abc.product')}</th>
              <th className="num">{t('report.abc.cost')}</th>
              <th className="num">{t('report.dead.onHand')}</th>
              <th className="num">{t('report.abc.turnover')}</th>
              <th>{t('report.abc.class')}</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.productId}>
                <td>
                  <span className="stack" style={{ gap: 0 }}>
                    <Link to={`/products/${row.productId}`}>{row.productName ?? row.sku ?? '—'}</Link>
                    {row.sku && <span className="mono muted t-xs">{row.sku}</span>}
                  </span>
                </td>
                <td className="num"><Money value={row.totalCost} /></td>
                <td className="num"><NumberValue value={row.onHand} /></td>
                {/* null при нулевом остатке: закончившийся товар не самый оборачиваемый. */}
                <td className="num strong"><NumberValue value={row.turnoverRatio} digits={2} /></td>
                <td>
                  <span className="mono strong">{row.combinedClass}</span>
                  {row.needsSafetyStock && (
                    <span className="flag flag-warning" style={{ marginLeft: 6 }}>
                      <Icon name="alert" />
                      {t('wr.safety')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager {...pager} />
      <div className="panel-f secondary"><span>{t('wr.turn.footer')}</span></div>
    </>
  )
}

const ABC: AbcClass[] = ['A', 'B', 'C']
const XYZ: Exclude<XyzClass, 'UNKNOWN'>[] = ['X', 'Y', 'Z']

/** Политика запаса по клетке матрицы — словами, а не только цветом. */
function cellLevel(abc: AbcClass, xyz: Exclude<XyzClass, 'UNKNOWN'>): { lv: number; key: string } {
  if (abc === 'A' && xyz !== 'Z') return { lv: 1, key: 'wr.mx.key' }
  if (abc === 'C' && xyz === 'Z') return { lv: 4, key: 'wr.mx.out' }
  if (xyz === 'Z' || (abc === 'C' && xyz === 'Y')) return { lv: 3, key: 'wr.mx.order' }
  return { lv: 2, key: 'wr.mx.std' }
}

function Matrix({ rows }: { rows: InventoryClassification[] }) {
  const { t, locale } = useApp()
  if (rows.length === 0) return <StateView kind="empty" compact title={t('wr.abc.empty')} />
  const totalCost = rows.reduce((sum, row) => sum + (num(row.totalCost) ?? 0), 0)
  const unknown = rows.filter((row) => row.xyzClass === 'UNKNOWN').length
  const cell = (abc: AbcClass, xyz: XyzClass) => rows.filter((row) => row.abcClass === abc && row.xyzClass === xyz)
  const byLevel = new Map<number, number>()
  return (
    <div className="panel-b stack" style={{ gap: 12 }}>
      <div className="matrix3" role="table" aria-label={t('wr.mx.label')}>
        <span role="presentation" />
        {XYZ.map((xyz) => (
          <div key={xyz} className="mx-h stack" role="columnheader" style={{ gap: 0 }}>
            <span>{xyz}</span>
            <span className="t-xs muted" style={{ fontWeight: 400 }}>{t(`wr.xyz.${xyz}`)}</span>
          </div>
        ))}
        {ABC.map((abc) => (
          <div key={abc} role="row" style={{ display: 'contents' }}>
            <div className="mx-h stack" role="rowheader" style={{ gap: 0 }}>
              <span>{abc}</span>
            </div>
            {XYZ.map((xyz) => {
              const items = cell(abc, xyz)
              const share = totalCost > 0 ? (items.reduce((s, r) => s + (num(r.totalCost) ?? 0), 0) / totalCost) * 100 : null
              const level = cellLevel(abc, xyz)
              byLevel.set(level.lv, (byLevel.get(level.lv) ?? 0) + items.length)
              return (
                <div key={xyz} className={`mx-cell lv-${level.lv}`} role="cell">
                  <span className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="k">{abc}{xyz}</span>
                    <span className="t-xs strong">{t(level.key)}</span>
                  </span>
                  <span className="n">{formatNumber(items.length, locale)}</span>
                  <span className="s">{percentText(share, locale)}</span>
                </div>
              )
            })}
          </div>
        ))}
      </div>
      <div className="legend">
        {[1, 2, 3, 4].map((lv) => (
          <span key={lv} className="row" style={{ gap: 6 }}>
            <span className={`legend-sw mx-cell lv-${lv}`} style={{ padding: 0 }} />
            {t(['wr.mx.key', 'wr.mx.std', 'wr.mx.order', 'wr.mx.out'][lv - 1]!)} — {formatNumber(byLevel.get(lv) ?? 0, locale)}
          </span>
        ))}
      </div>
      {unknown > 0 && <p className="t-sm secondary" style={{ margin: 0 }}>{t('wr.mx.unknown', { count: unknown })}</p>}
    </div>
  )
}

// ------------------------------------------------------------------ неликвид

/**
 * Названия складов — через список остатков: у склада своего списка нет, а колонка
 * `warehouse_id` остатка несёт название рядом (`warehouse_id__label`).
 */
function useWarehouseNames(ids: (string | null)[]): Map<string, string> {
  const unique = Array.from(new Set(ids.filter((id): id is string => Boolean(id)))).sort()
  const query = useQuery({
    queryKey: ['wr-warehouses', unique],
    enabled: unique.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: async ({ signal }) => {
      const names = new Map<string, string>()
      for (const id of unique) {
        const page = await fetchPage(
          'warehouse.stock',
          { columns: ['warehouse_id'], filters: [{ column: 'warehouse_id', operator: 'eq', value: id }], sort: [], pageSize: 1 },
          null,
          signal,
        )
        const label = page.rows[0]?.warehouse_id__label
        if (label) names.set(id, String(label))
      }
      return names
    },
  })
  return query.data ?? new Map()
}

function DeadStockPanel({
  allowed,
  query,
  since,
}: {
  allowed: boolean
  query: QueryLike<DeadStockRow[]>
  since: string
}) {
  const { t, locale } = useApp()
  const rows = query.data ?? []
  const warehouses = useWarehouseNames(rows.map((row) => row.warehouseId))
  const { visible, pager } = useClientPages(rows, 25)
  const today = Date.now()
  return (
    <section className="panel" aria-labelledby="wr-dead" style={{ overflow: 'hidden' }}>
      <div className="panel-h">
        <span className="h2" id="wr-dead">{t('wr.dead.title', { days: DEAD_DAYS })}</span>
        {query.data && <span className="count">{t('wr.dead.count', { count: rows.length, since: formatDate(since, locale) })}</span>}
      </div>
      {!allowed ? (
        <StateView kind="forbidden" compact title={t('state.forbidden.title')} />
      ) : query.isLoading ? (
        <SkeletonLines rows={5} />
      ) : query.error ? (
        <QueryState compact error={query.error} errorTitle={t('wr.dead.error')} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <StateView kind="empty" compact title={t('wr.dead.empty')} />
      ) : (
        <>
          <div className="wa-scroll">
            <table className="tbl" aria-label={t('wr.dead.title', { days: DEAD_DAYS })}>
              <thead>
                <tr>
                  <th>{t('report.abc.product')}</th>
                  <th>{t('wr.warehouse')}</th>
                  <th className="num">{t('report.dead.onHand')}</th>
                  <th>{t('wr.dead.last')}</th>
                  <th className="num">{t('wr.dead.days')}</th>
                  <th>{t('report.dead.lastOutbound')}</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => {
                  const last = row.lastMovementAt ? Date.parse(row.lastMovementAt) : null
                  const days = last ? Math.floor((today - last) / 86_400_000) : null
                  return (
                    <tr key={`${row.productId}-${row.warehouseId}`}>
                      <td>
                        <span className="stack" style={{ gap: 0 }}>
                          <Link to={`/products/${row.productId}`}>{row.productName ?? row.sku ?? '—'}</Link>
                          {row.sku && <span className="mono muted t-xs">{row.sku}</span>}
                        </span>
                      </td>
                      <td>{row.warehouseId ? warehouses.get(row.warehouseId) ?? <ShortId id={row.warehouseId} /> : '—'}</td>
                      <td className="num strong"><NumberValue value={row.onHand} /></td>
                      <td>{row.lastMovementAt ? formatDate(row.lastMovementAt.slice(0, 10), locale) : <span className="muted">—</span>}</td>
                      <td className="num">
                        {days == null ? (
                          <span className="muted">—</span>
                        ) : (
                          <span className="flag flag-warning">
                            <Icon name="clock" />
                            {formatNumber(days, locale)}
                          </span>
                        )}
                      </td>
                      <td>
                        {row.neverShipped ? (
                          <span className="badge b-warning">
                            <Icon name="alert" />
                            {t('report.dead.never')}
                          </span>
                        ) : row.lastOutboundAt ? (
                          formatDate(row.lastOutboundAt.slice(0, 10), locale)
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <Pager {...pager} />
        </>
      )}
    </section>
  )
}

// ------------------------------------------------------------------ сборщики

function PickersPanel({ allowed, query, periodText }: { allowed: boolean; query: QueryLike<PickerPerformance[]>; periodText: string }) {
  const { t, locale } = useApp()
  const rows = (query.data ?? []).slice().sort((a, b) => (num(b.linesPerHour) ?? -1) - (num(a.linesPerHour) ?? -1))
  const { names } = useNames('hr.employee', ['last_name', 'first_name'], rows.map((row) => row.employeeId))
  const max = Math.max(1, ...rows.map((row) => num(row.linesPerHour) ?? 0))
  return (
    <section className="panel" aria-labelledby="wr-pick" style={{ overflow: 'hidden' }}>
      <div className="panel-h">
        <span className="h2" id="wr-pick">{t('wr.pick.title')}</span>
        <span className="count">{periodText}</span>
      </div>
      {!allowed ? (
        <StateView kind="forbidden" compact title={t('state.forbidden.title')} />
      ) : query.isLoading ? (
        <SkeletonLines rows={5} />
      ) : query.error ? (
        <QueryState compact error={query.error} errorTitle={t('wr.pick.error')} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <StateView kind="empty" compact title={t('wr.pick.empty')} />
      ) : (
        <div className="wa-scroll">
          <table className="tbl" aria-label={t('wr.pick.title')}>
            <thead>
              <tr>
                <th>{t('report.picker.employee')}</th>
                <th style={{ width: '30%' }}>{t('report.picker.perHour')}</th>
                <th className="num">{t('report.picker.tasks')}</th>
                <th className="num">{t('report.picker.lines')}</th>
                <th className="num">{t('wr.pick.hours')}</th>
                <th className="num">{t('report.picker.shortfall')}</th>
                <th className="num">{t('wr.pick.skipped')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const name = names.get(row.employeeId)
                const rate = num(row.linesPerHour)
                return (
                  <tr key={row.employeeId}>
                    <td>
                      <span className="row" style={{ gap: 6 }}>
                        <span className="ava">{initials(name)}</span>
                        {name ? <Link to={`/employees/${row.employeeId}`}>{name}</Link> : <ShortId id={row.employeeId} />}
                      </span>
                    </td>
                    <td>
                      {/* null при нулевом времени: иначе самым быстрым был бы сборщик без отметок. */}
                      <span className="row" style={{ gap: 10 }}>
                        <span className="hbar-track" style={{ flex: 1, display: 'block' }}>
                          <span className="hbar" style={{ display: 'block', width: `${((rate ?? 0) / max) * 100}%`, height: 8 }} />
                        </span>
                        <span className="num strong" style={{ minWidth: 36 }}><NumberValue value={row.linesPerHour} digits={1} /></span>
                      </span>
                    </td>
                    <td className="num">{formatNumber(row.taskCount, locale)}</td>
                    <td className="num">{formatNumber(row.lineCount, locale)}</td>
                    <td className="num">{row.workingSeconds > 0 ? formatNumber(Math.round((row.workingSeconds / 3600) * 10) / 10, locale) : <span className="muted">—</span>}</td>
                    <td className="num">{percentText(num(row.shortfallPercent), locale)}</td>
                    <td className="num">{formatNumber(row.skippedLineCount, locale)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

// ------------------------------------------------------------------ поставщики

function SuppliersPanel({ allowed, query, periodText }: { allowed: boolean; query: QueryLike<SupplierQuality[]>; periodText: string }) {
  const { t, locale } = useApp()
  const rows = (query.data ?? []).slice().sort((a, b) => (num(b.defectPercent) ?? 0) - (num(a.defectPercent) ?? 0))
  const maxDefect = Math.max(2, ...rows.map((row) => num(row.defectPercent) ?? 0))
  return (
    <section className="panel" aria-labelledby="wr-sup" style={{ overflow: 'hidden' }}>
      <div className="panel-h">
        <span className="h2" id="wr-sup">{t('wr.sup.title')}</span>
        <span className="count">{periodText}</span>
      </div>
      {!allowed ? (
        <StateView kind="forbidden" compact title={t('state.forbidden.title')} />
      ) : query.isLoading ? (
        <SkeletonLines rows={5} />
      ) : query.error ? (
        <QueryState compact error={query.error} errorTitle={t('wr.sup.error')} onRetry={() => void query.refetch()} />
      ) : rows.length === 0 ? (
        <StateView kind="empty" compact title={t('wr.sup.empty')} />
      ) : (
        <>
          <div className="wa-scroll">
            <table className="tbl" aria-label={t('wr.sup.title')}>
              <thead>
                <tr>
                  <th>{t('report.supplier.name')}</th>
                  <th className="num">{t('report.supplier.receipts')}</th>
                  <th className="num">{t('wr.sup.accepted')}</th>
                  {/* Брак и недопоставка раздельно: это разные претензии к поставщику. */}
                  <th>{t('wr.sup.defect')}</th>
                  <th className="num">{t('report.supplier.shortfall')}</th>
                  <th className="num">{t('wr.sup.withDefects')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const defect = num(row.defectPercent)
                  return (
                    <tr key={row.supplierId}>
                      <td className="wrap">
                        <span className="stack" style={{ gap: 0 }}>
                          <Link to={`/counterparties/${row.supplierId}`}>{row.supplierName ?? row.inn ?? '—'}</Link>
                          {row.inn && <span className="mono t-xs muted">{row.inn}</span>}
                        </span>
                      </td>
                      <td className="num">{formatNumber(row.receiptCount, locale)}</td>
                      <td className="num"><NumberValue value={row.acceptedQuantity} /></td>
                      <td>
                        <span className="row" style={{ gap: 8 }}>
                          <span className="bullet" style={{ width: 90, flex: 'none' }} role="img" aria-label={t('wr.sup.defectAria', { pct: percentText(defect, locale) })}>
                            <span className="fill" style={{ width: `${Math.min(100, ((defect ?? 0) / maxDefect) * 100)}%` }} />
                          </span>
                          <span className="stack" style={{ gap: 0 }}>
                            <span className="strong">{percentText(defect, locale)}</span>
                            <span className="t-xs muted"><NumberValue value={row.rejectedQuantity} /> {t('wr.pcs')}</span>
                          </span>
                        </span>
                      </td>
                      <td className="num"><NumberValue value={row.shortfallQuantity} /></td>
                      <td className="num">{percentText(num(row.receiptsWithDefectsPercent), locale)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          <div className="panel-f secondary"><span>{t('wr.sup.footer')}</span></div>
        </>
      )}
    </section>
  )
}
